import { refusalCode, requestFailure } from '@/api'

/**
 * What the splash says when GitHub sends somebody back with an error instead of a session. The API redirects
 * here with `?error=…`, and an invitation that could not be accepted carries `code` too (#620) — an invitation
 * that is dead has to say which way it is dead, rather than reading as a blank sign-in failure.
 */
const INVITATION_REASONS: Record<string, string> = {
    invitation_expired: 'That invitation has expired. Ask whoever invited you for a fresh link.',
    invitation_revoked: 'That invitation was withdrawn. Ask whoever invited you for a fresh link.',
    invitation_already_used: 'That invitation has already been accepted. Sign in instead.',
    invitation_not_found: 'That invitation link is not one we issued.',
    invitation_enterprise_unavailable: 'The enterprise that invited you is no longer open.',
}

/** The provider a failed sign-in went through (#636), as the splash names it. GitHub when the API names none. */
const PROVIDER_NAMES: Record<string, string> = {
    github: 'GitHub',
    google: 'Google',
    microsoft: 'Microsoft',
    email: 'Email code',
}

/** What the two refusals sign-up retired (#2120) read as, when an old link still carries one. */
const STALE_REFUSAL = 'That sign-in did not finish. Try again.'

/**
 * Where somebody reaches Zero Human support (#1627): the public contact address the legal pages already give. A closed
 * enterprise can only be reopened through support, so every place that says so names the address too.
 */
export const ZERO_HUMAN_SUPPORT_EMAIL = 'hello@zerohuman.com'

/**
 * What the signed-in shell says when an owner closed one enterprise but still holds another open one (#1627). They
 * stay signed in to the open one, so the sign-in screen's message never shows; this line says the close happened.
 */
export function closedEnterpriseNotice(openEnterpriseName: string): string {
    return `That enterprise is now closed: nobody can sign in to it and nothing runs. You are signed in to ${openEnterpriseName}, which is not affected. To reopen the closed one within 30 days, email Zero Human support at ${ZERO_HUMAN_SUPPORT_EMAIL}.`
}

export function signInErrorMessage(params: URLSearchParams): string | null {
    const error = params.get('error')
    if (!error) {
        return null
    }
    if (error === 'invitation') {
        const code = params.get('code') ?? ''
        return INVITATION_REASONS[code] ?? 'That invitation could not be accepted.'
    }
    if (error === 'enterprise_closed') {
        // #1627: the enterprise was closed, by its owner just now or earlier: nobody signs in to it any more.
        return `This enterprise is closed. Nobody can sign in to it, and nothing runs. To reopen it within 30 days, email Zero Human support at ${ZERO_HUMAN_SUPPORT_EMAIL}.`
    }
    if (error === 'forbidden' || error === 'no_account') {
        // #2120: sign-up is open, so the API no longer turns anybody away for having no account. An old link or a
        // bookmark can still carry one of these, and it reads as a sign-in to try again, never as a refusal.
        return STALE_REFUSAL
    }
    const provider = PROVIDER_NAMES[params.get('provider') ?? ''] ?? 'GitHub'
    return `${provider} sign-in failed.`
}

/**
 * What the account page says after adding a way to sign in (#636). The API sends the browser back with
 * `?linked=<provider>` or `?link_error=<code>&provider=<provider>`.
 */
const LINK_REASONS: Record<string, string> = {
    identity_held_by_another_person:
        'That sign-in already belongs to somebody else here, so it was not added. Two people are never merged.',
    link_needs_session: 'Your session ended before that finished. Sign in again, then try once more.',
    link_not_started_here: 'That did not start from this page, so nothing was added. Try again from here.',
}

export function linkResultMessage(params: URLSearchParams): { ok: boolean; message: string } | null {
    const linked = params.get('linked')
    if (linked) {
        return {
            ok: true,
            message: `${PROVIDER_NAMES[linked] ?? 'That way in'} added. You can sign in with it from now on.`,
        }
    }
    const failure = params.get('link_error')
    if (!failure) {
        return null
    }
    const provider = PROVIDER_NAMES[params.get('provider') ?? ''] ?? 'That provider'
    return { ok: false, message: LINK_REASONS[failure] ?? `${provider} could not be added. Try again.` }
}

/** The code the API refuses a wrong, used or expired code with, beside its 401. */
const WRONG_CODE_REFUSAL = 'code_invalid'

/**
 * Whether a failed code check refused the code itself, the one failure a new code can fix. The sign-in screen and the
 * account page share this rule (#522), so the two cannot drift apart.
 *
 * The API answers a wrong, used or expired code with 401 `code_invalid`. Any other refusal (no account for that
 * address, an invitation that no longer stands, a server fault) or a request that never reached the API (`fetch`
 * rejects with a TypeError) would fail the same way with a new code, and every try spends one of the address's codes.
 * An Error with no status that is not a network failure keeps the wrong-code reading, as does a failure that is not an
 * Error at all (each screen's own fallback, "That code did not work.").
 */
export function codeRefused(caught: unknown): boolean {
    if (!(caught instanceof Error)) return true
    if (refusalCode(caught) === WRONG_CODE_REFUSAL) return true
    const failure = requestFailure(caught)
    if (failure) return failure.status === 401
    return !(caught instanceof TypeError)
}
